import { DOCUMENT, inject, Injectable } from '@angular/core';

/** The href of a "Cookie settings" link, so it can be told from the links beside it and from other arrivals. */
export const COOKIE_SETTINGS_HREF = '#cookie-settings';

/**
 * What the app reads off `window` from the cookie consent kit (`src/assets/consent/`, vendored from
 * anothertrip-design-system/consent/). The kit is plain JavaScript that every index shell loads before
 * Angular boots, so it carries no types of its own. Its full API is in its README.
 */
export type ConsentKitWindow = Window & {
  atConsent?: { open: () => void };
  atConsentConfig?: { policyUrl?: string };
};

/**
 * The app's one way into the consent kit (AT-2748).
 *
 * The kit owns the banner, the Cookie settings dialog, the stored choice and Google Consent Mode. The app
 * only has to give people a way back into the dialog. GTM fires its tags from the kit's own
 * `at_consent_update` event, so analytics needs nothing from here.
 */
@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly window = inject(DOCUMENT).defaultView as ConsentKitWindow | null;

  /**
   * Opens the kit's Cookie settings dialog. With the kit missing (an ad blocker, a deploy that dropped the
   * asset) the visitor still gets an answer: the cookie policy, from the index shell's inline config. The
   * inline default has left everything denied either way.
   *
   * The kit records whatever has focus as where to return when its dialog closes, so a caller that is about
   * to remove the focused control hands focus on first.
   */
  openSettings(): void {
    const kit = this.window?.atConsent;
    if (kit) {
      kit.open();
      return;
    }

    const policyUrl = this.window?.atConsentConfig?.policyUrl;
    if (policyUrl) {
      this.window?.location.assign(policyUrl);
    }
  }

  /**
   * A Cookie settings link opened in a new tab (a middle-click, "Open in new tab") arrives as
   * `/#cookie-settings`, with no click for the footer to answer, so the app does it on start. The kit is
   * already there: its script is synchronous and comes before the app's. The hash goes first, so a reload
   * does not reopen the dialog and Back from the policy page does not bounce straight out again.
   */
  openIfLinked(): void {
    const view = this.window;
    if (view?.location.hash !== COOKIE_SETTINGS_HREF) {
      return;
    }

    view.history.replaceState(view.history.state, '', `${view.location.pathname}${view.location.search}`);
    this.openSettings();
  }
}
